import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useAppConfig } from '@/hooks/use-session'
import { signInWithMicrosoft } from '@/lib/auth-client'
import appIcon from '@/assets/icon.png'

function MicrosoftLogo(): React.JSX.Element {
  return (
    <svg viewBox="0 0 21 21" className="size-4" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

export function SignInPage({ error }: { error?: string }): React.JSX.Element {
  const config = useAppConfig()
  const [pending, setPending] = useState(false)
  const [signInError, setSignInError] = useState<string | null>(null)
  const callbackError = new URLSearchParams(window.location.search).get('error')

  async function signIn(): Promise<void> {
    setPending(true)
    setSignInError(null)
    try {
      await signInWithMicrosoft()
    } catch {
      setSignInError('Sign-in could not be started. Please try again.')
      setPending(false)
    }
  }

  const message =
    signInError ??
    (callbackError
      ? 'Sign-in was not completed. Only Coneno accounts with access to c-sheet can sign in.'
      : error)

  return (
    <main className="grid min-h-svh place-items-center bg-muted/40 p-6">
      <section className="flex w-full max-w-sm flex-col items-center gap-6 rounded-xl border bg-card p-8 text-center shadow-sm">
        <img src={appIcon} alt="" className="size-12" />
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold tracking-tight">Sign in to c-sheet</h1>
          <p className="text-sm text-muted-foreground">Use your Coneno Microsoft account.</p>
        </div>
        {config.data && !config.data.microsoftSignIn ? (
          <p className="text-sm text-destructive">
            Microsoft sign-in is not configured on this server. Set the MICROSOFT_* variables.
          </p>
        ) : (
          <Button className="w-full" onClick={signIn} disabled={pending || config.isPending}>
            <MicrosoftLogo />
            {pending ? 'Redirecting…' : 'Sign in with Microsoft'}
          </Button>
        )}
        {config.error && <p className="text-sm text-destructive">{config.error.message}</p>}
        {message && (
          <p role="alert" className="text-sm text-destructive">
            {message}
          </p>
        )}
      </section>
    </main>
  )
}
